import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LotusIcon from "@/components/icons/LotusIcon";
import SpiritualDiagram from "@/components/SpiritualDiagram";
import WisdomCapsulesSidePane from "@/components/WisdomCapsules";
import { Card, CardContent } from "@/components/ui/card";
import { Volume2, Sparkles, Layers, Zap, ArrowRight, Briefcase, Heart } from "lucide-react";

const WisdomBoard = () => {
  const coreValues = [
    {
      icon: Volume2,
      title: "Silence as Foundation",
      description: "Cultivate profound quietude to witness truth beyond thoughts—transcending stress, doubt, and discord."
    },
    {
      icon: Sparkles,
      title: "Wonderment as Destiny",
      description: "Awaken innate spiritual awe, revealing purpose, joy, and unbreakable flow in relationships and work."
    },
    {
      icon: Layers,
      title: "Holistic Integration",
      description: "Honor all five koshas (Annamaya to Anandamaya)—physical vitality to bliss consciousness—balanced with masculine-feminine energies (Shiva-Shakti union for creative harmony), 3 Gunas (Sattva for clarity, Rajas for action, Tamas for inertia), and 6 Vikaras (aligning kama, krodha, lobha, moha, mada, matsarya) for complete, wholesome transformation."
    },
    {
      icon: Zap,
      title: "Practical Spirituality",
      description: "Ancient wisdom meets modern action; no escapism, only empowered living."
    }
  ];

  const threeAs = [
    {
      letter: "A",
      title: "Awareness",
      description: "Silences patterns (burnout, blame)"
    },
    {
      letter: "A",
      title: "Alignment",
      description: "Harmonizes koshas with purpose"
    },
    {
      letter: "A",
      title: "Awakening",
      description: "Unleashes wonder, birthing effortless wisdom-aligned living"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="wisdom-board-page">
      <Header />

      {/* 2-column layout: sticky Wisdom Capsules pane (left) + existing content (right) */}
      <div className="lg:flex lg:items-start">
        <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto p-6">
          <WisdomCapsulesSidePane />
        </aside>

        <main className="flex-1 min-w-0">
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary mb-6 font-bold">
              Wisdom Board
            </h1>
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <p className="text-lg md:text-xl text-foreground/80 leading-relaxed mt-6 font-medium">
              Our philosophy rests on <span className="font-semibold text-primary">SIWON</span>—the sacred fusion of{" "}
              <span className="font-semibold text-primary">Silence</span> (inner stillness dissolving ego noise) and{" "}
              <span className="font-semibold text-primary">Wonderment</span> (unfolding the divine spark within every soul). 
              This eternal truth, drawn from Vedanta, Bhagavad Gita, and Pancha Koshas, aligns life's chaos with cosmic harmony.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 mandala-bg">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif text-primary text-center mb-12 font-bold">
            Core Values
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {coreValues.map((value, index) => (
              <Card key={index} className="card-spiritual hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-serif text-primary mb-3 font-bold">{value.title}</h3>
                      <p className="text-foreground/80 leading-relaxed font-medium">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lotus Divider */}
      <div className="container">
        <div className="lotus-divider">
          <LotusIcon size={40} />
        </div>
      </div>

      {/* Holistic Framework Diagram Section */}
      <section className="py-16 bg-gradient-to-b from-background to-accent/5">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif text-primary text-center mb-4">
            Holistic Framework
          </h2>
          <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
            Explore the interconnected wisdom of ancient teachings—the three qualities of nature, 
            the five sheaths of being, and the divine balance of consciousness and energy.
          </p>
          <SpiritualDiagram />
        </div>
      </section>

      {/* Lotus Divider */}
      <div className="container">
        <div className="lotus-divider">
          <LotusIcon size={40} />
        </div>
      </div>

      {/* Philosophy in Action Section */}
      <section className="py-16 bg-gradient-to-b from-background to-primary/5">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif text-primary text-center mb-6 font-bold">
            Philosophy in Action
          </h2>
          <p className="text-center text-foreground/70 max-w-3xl mx-auto mb-12 font-medium">
            The 3 A's Framework weaves SIWON into daily life
          </p>

          {/* 3 A's Framework */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
            {threeAs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-serif font-bold text-primary">{item.letter}</span>
                </div>
                <h3 className="text-xl font-serif text-primary mb-2 font-bold">{item.title}</h3>
                <p className="text-foreground/70 text-sm font-medium">{item.description}</p>
                {index < 2 && (
                  <ArrowRight className="w-5 h-5 text-accent mx-auto mt-4 hidden md:block rotate-0" />
                )}
              </div>
            ))}
          </div>

          {/* Outcomes */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="card-spiritual border-l-4 border-l-primary">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-serif text-primary font-bold">For Professionals</h3>
                </div>
                <p className="text-foreground/80 font-medium">
                  From grind to inspired mastery—transform burnout into purposeful, wisdom-aligned success.
                </p>
              </CardContent>
            </Card>
            <Card className="card-spiritual border-l-4 border-l-accent">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-6 h-6 text-accent" />
                  <h3 className="text-lg font-serif text-primary font-bold">For Couples</h3>
                </div>
                <p className="text-foreground/80 font-medium">
                  From survival to soul union—transcend blame games into mutual wonder and harmony.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Closing Tagline */}
          <div className="text-center mt-16">
            <p className="text-2xl md:text-3xl font-serif text-gradient-peacock italic font-semibold">
              "Live SIWON—where silence births eternal wonder."
            </p>
          </div>
        </div>
      </section>

        </main>
      </div>

      {/* Mobile-only floating trigger + drawer for Wisdom Capsules */}
      <div className="lg:hidden">
        <WisdomCapsulesSidePane />
      </div>

      <Footer onStartQuiz={() => {}} />
    </div>
  );
};

export default WisdomBoard;
